import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { TournamentPageLayout } from '../../../layouts/mainMenu/tournament/TournamentPageLayout';
import { TournamentTabBarView } from './TournamentTabBarView';
import type { MenuPage } from '../MainMenuView';
import type { Tournament } from '../../app/tournaments/types';
import { TournamentCategory } from '../../app/tournaments/TournamentService';
import { LoadingSpinnerView } from '../../views/LoadingSpinnerView';
import ScrollBehaviour, { Direction } from '../../utils/ScrollBehaviour';
import { TournamentBlankPageType, TournamentTabView } from './TournamentTabView';
import type { PlatformUser, TournamentData } from '../../app/platform/Platform';
import type { AppStateController } from '../../app/state/AppStateController';

interface TabProps {
  scrollY: number;
  scrollX: number;
  view: TournamentTabView | null;
}

interface TournamentPageViewConfig {
  state: AppStateController;
  currentUser: PlatformUser;
  tournamentGetter: (tab: TournamentCategory) => Promise<Tournament[]>;
  activeTournament: TournamentData | null;
}

export class TournamentPageView extends Container implements MenuPage {
  public readonly onTabClicked = new Signal<(tab: TournamentCategory) => void>();

  public readonly onTournamentJoinClicked = new Signal<(tournamentId: string) => void>();

  public readonly onTournamentCreateClicked = new Signal();

  private layout: TournamentPageLayout | null = null;

  private barView: TournamentTabBarView | null = null;

  private scrollBehaviour: ScrollBehaviour | null = null;

  private initialized = false;

  private readonly tabs: Partial<Record<TournamentCategory, TabProps>> = {};

  private currentTab: TournamentCategory = TournamentCategory.Global;

  private isHorizontal = false;

  constructor(private readonly config: TournamentPageViewConfig) {
    super();
  }

  public isAboveTopBar(): boolean {
    return true;
  }

  public show(): void {
    this.init();
    this.resetTabs();
    this.visible = true;
    this.interactiveChildren = true;
    this.scrollBehaviour?.setActive(true);
    this.setTab(this.currentTab);
  }

  private resetTabs(): void {

    const tabContentMap = this.getTabContentMap();
    if (!tabContentMap) return;

    Object.values(tabContentMap).forEach((container) => {
      const removed = container.removeChildren();
      removed.forEach((child) => child.destroy({ children: true }));
    });

    (Object.keys(this.tabs) as TournamentCategory[]).forEach((tab) => {
      delete this.tabs[tab];
    });
  }

  public async hide(animationPromise: Promise<void>): Promise<void> {
    this.interactiveChildren = false;
    this.scrollBehaviour?.setActive(false);
    await animationPromise;
    this.visible = false;
  }

  public resize(width: number, _: number): void {
    if (!this.layout) return;

    const { state } = this.config;

    const isHorizontal = state.isHorizontalLayoutActive();

    this.isHorizontal = isHorizontal;

    const { background, backgroundLandscape, bar } = this.layout;
    background.props({ width, visible: !isHorizontal });
    backgroundLandscape.props({ width, visible: isHorizontal });

    bar.props({
      x: width * -0.5,
      width,
    });

    this.updateScroll();
  }

  private init(): void {
    if (this.initialized) return;
    this.layout = new TournamentPageLayout();

    const { root, spinnerSlot, scrollInput } = this.layout;
    this.addChild(root);

    scrollInput.cursor = 'default';
    this.barView = new TournamentTabBarView(this.layout, TournamentCategory.Global);
    this.barView.onTabClicked.connect((tab) => {
      this.onTabClicked.emit(tab);
    });

    new LoadingSpinnerView({ dotColor: '#933420' }).props({
      parent: spinnerSlot,
    });

    this.scrollBehaviour = new ScrollBehaviour({
      inputLayer: this.layout.scrollInput,
      renderer: this.pixi.renderer,
      target: this.layout.leaderboardsContainer,
      verticalScroll: { min: 0, max: 0 },
      rubberBanding: true,
      wheelDirection: Direction.Vertical,
    });

    this.initialized = true;
    this.currentTab = TournamentCategory.You;
    this.setTab(this.currentTab);
  }

  private getTabContentMap(): Record<TournamentCategory, Container> | null {
    if (!this.layout) return null;

    const { globalContent, friendsContent, youContent } = this.layout;

    return {
      [TournamentCategory.Global]: globalContent,
      [TournamentCategory.Friends]: friendsContent,
      [TournamentCategory.You]: youContent,
    };
  }

  private initTab(tab: TournamentCategory): void {
    if (this.destroyed) return;
    if (!this.layout) return;
    if (this.tabs[tab]) return;

    const contentParent = this.getTabContentMap()?.[tab];
    if (!contentParent) return;

    const { currentUser, tournamentGetter, activeTournament } = this.config;

    const tabProps: TabProps = {
      scrollY: 0,
      scrollX: 0,
      view: null,
    };

    this.tabs[tab] = tabProps;

    const tabView = new TournamentTabView({
      currentUser,
      activeTournament,
      tournamentPromise: tournamentGetter(tab),
      blankPageType:
        tab === TournamentCategory.You ? TournamentBlankPageType.CreateTournament : TournamentBlankPageType.Empty,
    }).props({
      parent: contentParent,
    });

    tabView.onTournamentJoinClicked.connect((tournamentId) => {
      this.onTournamentJoinClicked.emit(tournamentId);
    });

    tabView.onTournamentCreateClicked.connect(() => {
      this.onTournamentCreateClicked.emit();
    });

    tabView.onViewReady.connect(() => {
      tabProps.view = tabView;
      if (this.currentTab === tab) {
        this.setTab(tab);
      }
    });
  }

  public setTab(tab: TournamentCategory): void {
    const prevTab = this.currentTab;
    this.currentTab = tab;
    const tabContentMap = this.getTabContentMap();

    if (!this.initialized) return;
    if (!tabContentMap) return;
    if (!this.layout || !this.barView || !this.scrollBehaviour) return;

    const { spinnerSlot, leaderboardsContainer } = this.layout;

    this.barView.setActiveTab(tab);

    const tabProps = this.tabs[tab];
    const tabView = tabProps?.view;

    if (!tabProps) {
      this.initTab(tab);
    }

    const prevProps = this.tabs[prevTab];
    if (prevProps) {
      prevProps.scrollY = leaderboardsContainer.y;
      prevProps.scrollX = leaderboardsContainer.x;
    }

    Object.entries(tabContentMap).forEach(([contentTab, content]) => {
      content.visible = (contentTab as TournamentCategory) === tab;
    });

    this.updateScroll(true);
    spinnerSlot.visible = !tabView;
  }

  private updateScroll(adjustContainer = false): void {
    const tabProps = this.tabs[this.currentTab];
    const tabView = tabProps?.view;
    if (!tabProps || !tabView || !this.layout) return;

    if (adjustContainer) {
      this.layout.leaderboardsContainer.props({
        x: this.isHorizontal ? tabProps.scrollX : 0,
        y: this.isHorizontal ? 0 : tabProps.scrollY,
      });
    }

    tabView.setHorizontalLayout(this.isHorizontal);

    const minY = Math.min(0, -(tabView.height - this.pixi.renderer.height * 0.5));
    const minX = Math.min(0, -(tabView.width - this.pixi.renderer.width * 0.4));

    this.scrollBehaviour?.updateConfig({
      horizontalScroll: this.isHorizontal ? { max: 0, min: minX } : undefined,
      verticalScroll: this.isHorizontal ? undefined : { max: 0, min: minY },
      wheelDirection: this.isHorizontal ? Direction.Horizontal : Direction.Vertical,
    });
  }
}
